import { z } from 'zod';

import {
  createEntityAdapterSchema,
  EntityBaseSchema,
  EntityMetadataSchema,
  PythonicNameSchema,
} from '../../base/primitives';

// ============================================================================
// Property Schemas (Reusable across command, form, and projection schemas)
// ============================================================================

export const FunctionNameSchema = PythonicNameSchema.describe(
  'Function name following Python naming conventions. ' +
    'Must start with a letter or underscore, contain only alphanumerics and underscores, ' +
    'and cannot be a Python reserved word. ' +
    "Examples: 'get_customer_data', 'calculate_total', 'send_notification'",
);

export const FunctionDescriptionSchema = z
  .string({ required_error: 'Function description is required' })
  .describe(
    'Human-readable description of what this function does. ' +
      'Should clearly explain the purpose and behavior of the function. ' +
      "Example: 'Retrieves customer information from the CRM system'",
  );

export const FunctionCodeSchema = z
  .string({ required_error: 'Function code is required' })
  .min(1, { message: 'Function code is required' })
  .describe(
    'Python code for the function implementation. ' +
      'Must be valid Python code that can be executed in the function runtime. ' +
      "The function definition must ALWAYS include the argument 'conv: Conversation' as its first parameter, followed by the name and type of all additional parameters. " +
      'Correct example: `def function_name(conv: Conversation, param1: str, param2: int):` ' +
      'Incorrect examples that are NOT allowed: `def function_name(conv, param1: str):`, `def function_name(conv):`, `def function_name(conv, *args, **kwargs):`. ' +
      "The 'Conversation' type is provided by the runtime and does not need to be imported by your code. " +
      "You must not omit the 'Conversation' type annotation on 'conv'. " +
      "If you need to use 'requests', you should import it explicitly in your code. " +
      'To send any output back to the LLM (so that the agent can react to the function result), simply return it as a string from the function. ' +
      'The code should define the function logic and return appropriate values.',
  );

export const FunctionArchivedSchema = z
  .boolean({ required_error: 'Archived status is required' })
  .describe(
    'Whether this function is archived. ' +
      'Archived functions are not deleted but are hidden from normal use. ' +
      'Used to preserve historical functions without cluttering the active function list.',
  );

export const FunctionParameterTypeSchema = z
  .enum(['string', 'number', 'boolean', 'integer'], {
    required_error: 'Parameter type is required',
  })
  .describe(
    'Data type of the function parameter. ' +
      "'string' for text values, " +
      "'number' for floating-point numbers, " +
      "'integer' for whole numbers, " +
      "'boolean' for true/false values",
  );

export const FunctionParameterNameSchema = PythonicNameSchema.describe(
  'Name of the function parameter following Python naming conventions',
);

const parameterDescriptionDescription =
  'Description of what this parameter represents';

export const OptionalFunctionParameterDescriptionSchema = z
  .string()
  .optional()
  .describe(parameterDescriptionDescription);

export const FunctionParameterDescriptionSchema = z
  .string({ required_error: 'Parameter description is required' })
  .min(1, { message: 'Parameter description is required' })
  .describe(parameterDescriptionDescription);

export const FunctionErrorLinenoSchema = z
  .number({ required_error: 'Error line number is required' })
  .int()
  .min(0, { message: 'Line number must be 1 or greater' })
  .describe('Line number where the error occurred in the function code');

export const FunctionErrorMessageSchema = z
  .string({ required_error: 'Error message is required' })
  .min(1, { message: 'Error message is required' })
  .describe('Error message describing what went wrong');

export const FunctionErrorTextSchema = z
  .string({ required_error: 'Error text is required' })
  .min(1, { message: 'Error text is required' })
  .describe('The line of code where the error occurred');

export const DelayResponseMessageSchema = z
  .string({ required_error: 'Delay response message is required' })
  .min(1, { message: 'Delay response message is required' })
  .describe(
    'The message to play to the user during the function execution. ' +
      'Can include variable placeholders. ' +
      "Example: 'Please hold while I look that up for you'",
  );

export const DelayResponseDurationSchema = z
  .number({ required_error: 'Delay duration is required' })
  .int()
  .min(0, { message: 'Duration must be 0 or greater' })
  .describe(
    'Duration of the delay in seconds. ' +
      'How long to wait before saying the first interim message. ' +
      'Example: 2 for a 2-second delay',
  );

export const DelayResponseReferencesSchema = z
  .object({
    variables: z
      .record(z.boolean(), {
        required_error: 'Variable references are required',
      })
      .describe(
        'Map of variable IDs that this delay response references. ' +
          'Keys are variable IDs, values are always true. ' +
          'Tracks which variables this delay response uses.',
      ),
  })
  .describe(
    'References to variables used in the delay response message. ' +
      'Used to track which variables are used in the message.',
  );

export const LatencyControlEnabledSchema = z
  .boolean({ required_error: 'Latency control enabled status is required' })
  .describe('Whether latency control is enabled for this function');

export const LatencyControlInitialDelaySchema = z
  .number({ required_error: 'Initial delay is required' })
  .int()
  .min(0, { message: 'Initial delay must be 0 or greater' })
  .describe(
    'Initial delay in milliseconds before the first delay response is played. ' +
      'Example: 1000 for a 1-second delay before the first message',
  );

export const LatencyControlIntervalSchema = z
  .number({ required_error: 'Interval is required' })
  .int()
  .min(0, { message: 'Interval must be 0 or greater' })
  .describe(
    'Interval in milliseconds between subsequent delay responses. ' +
      'Example: 3000 to play a new message every 3 seconds',
  );

export const FunctionParameterSchema = EntityBaseSchema.merge(
  EntityMetadataSchema,
).extend({
  name: FunctionParameterNameSchema,
  description: FunctionParameterDescriptionSchema,
  type: FunctionParameterTypeSchema,
});

export const FunctionErrorSchema = z
  .object({
    lineno: FunctionErrorLinenoSchema,
    message: FunctionErrorMessageSchema,
    text: FunctionErrorTextSchema,
  })
  .describe(
    'Represents a syntax or runtime error in the function code. ' +
      'Contains information about where the error occurred and what the error was.',
  );

export const DelayResponseSchema = EntityBaseSchema.merge(EntityMetadataSchema)
  .extend({
    message: DelayResponseMessageSchema,
    duration: DelayResponseDurationSchema,
    references: DelayResponseReferencesSchema.optional(),
  })
  .describe(
    'A message to play to the user while waiting for a function to execute. ' +
      'Used to provide feedback during long-running operations.',
  );

export const LatencyControlSchema = z
  .object({
    enabled: LatencyControlEnabledSchema,
    delayResponses: createEntityAdapterSchema(DelayResponseSchema).describe(
      'Collection of delay response messages that can be played while waiting for the function',
    ),
    initialDelay: LatencyControlInitialDelaySchema,
    interval: LatencyControlIntervalSchema,
  })
  .merge(EntityMetadataSchema)
  .describe(
    'Controls how the system handles latency when calling this function. ' +
      'Allows playing messages to the user while waiting for slow function execution.',
  );

export const FunctionReferencesSchema = z
  .object({
    flowSteps: z
      .record(z.boolean(), {
        required_error: 'Flow step references are required',
      })
      .describe(
        'Map of flow step IDs that call this function. ' +
          'Keys are flow step IDs, values are always true. ' +
          'Tracks which flow steps use this function.',
      ),
    topics: z
      .record(z.boolean(), { required_error: 'Topic references are required' })
      .describe(
        'Map of topic IDs that reference this function. ' +
          'Keys are topic IDs, values are always true. ' +
          'Tracks which topics use this function.',
      ),
    stopKeywords: z
      .record(z.boolean(), {
        required_error: 'Stop keyword references are required',
      })
      .describe(
        'Map of stop keyword IDs that reference this function. ' +
          'Keys are stop keyword IDs, values are always true. ' +
          'Tracks which stop keywords use this function.',
      ),
    behaviour: z
      .record(z.boolean(), {
        required_error: 'Behaviour references are required',
      })
      .describe(
        'Map of behaviour IDs that reference this function. ' +
          'Keys are behaviour IDs, values are always true. ' +
          'Tracks which behaviours use this function.',
      ),
    variables: z
      .record(z.boolean(), {
        required_error: 'Variable references are required',
      })
      .describe(
        'Map of variable IDs that this function references. ' +
          'Keys are variable IDs, values are always true. ' +
          'Tracks which variables this function uses.',
      ),
  })
  .describe(
    'Tracks all references to and from this function. ' +
      'Prevents accidental deletion of functions that are in use. ' +
      'Automatically managed by the system when references are created or removed.',
  );

export const FunctionSchema = EntityBaseSchema.merge(EntityMetadataSchema)
  .extend({
    name: FunctionNameSchema,
    description: FunctionDescriptionSchema,
    parameters: createEntityAdapterSchema(FunctionParameterSchema).describe(
      'Collection of parameters that can be passed to this function. ' +
        'Each parameter defines a value that can be provided when calling the function.',
    ),
    code: FunctionCodeSchema,
    archived: FunctionArchivedSchema,
    errors: z
      .array(FunctionErrorSchema, {
        required_error: 'Errors array is required',
      })
      .describe(
        'List of syntax or runtime errors in the function code. ' +
          'Empty array means the function code is valid. ' +
          'Used to display validation feedback to users editing function code.',
      ),
    references: FunctionReferencesSchema,
    latencyControl: LatencyControlSchema,
  })
  .describe(
    'A custom Python function that can be called during conversation flows. ' +
      'Functions allow integrating custom business logic, API calls, or data processing.',
  );

export type FunctionParameter = z.infer<typeof FunctionParameterSchema>;
export type FunctionError = z.infer<typeof FunctionErrorSchema>;
export type DelayResponse = z.infer<typeof DelayResponseSchema>;
export type LatencyControl = z.infer<typeof LatencyControlSchema>;
export type DelayResponseReferences = z.infer<
  typeof DelayResponseReferencesSchema
>;
export type DelayResponseReference = keyof DelayResponseReferences;
export type FunctionReferences = z.infer<typeof FunctionReferencesSchema>;
export type FunctionReference = keyof FunctionReferences;

export type Function = z.infer<typeof FunctionSchema>;
export type FunctionParameterType = z.infer<typeof FunctionParameterTypeSchema>;
